import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ApiError, categoriesApi, itemsApi, locationsApi, verification } from '../api';
import { useAuth } from '../context/AuthContext';
import { canWrite } from '../lib/permissions';
import StorageBar from '../components/StorageBar';

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  const { data: items = [] } = useQuery({ queryKey: ['items', {}], queryFn: () => itemsApi.list() });
  const { data: categories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: categoriesApi.list,
  });
  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: locationsApi.list,
  });

  if (!user) return null;

  async function handleResend() {
    setResendStatus('sending');
    try {
      await verification.resend();
      setResendStatus('sent');
    } catch (err) {
      if (err instanceof ApiError && err.message === 'EMAIL_ALREADY_VERIFIED') {
        await refresh();
        setResendStatus('idle');
      } else {
        setResendStatus('error');
      }
    }
  }

  const lowStockItems = items.filter(
    (i) => i.lowStockThreshold && Number(i.quantity) <= Number(i.lowStockThreshold),
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          {t('dashboard.welcome', { name: user.name })}
        </h1>
        <p className="text-sm text-slate-600">{t('dashboard.subtitle')}</p>
      </div>

      {!user.emailVerified && (
        <div className="bg-amber-50 border border-amber-200 rounded p-4">
          <p className="font-medium text-amber-900">{t('dashboard.verifyBanner.title')}</p>
          <p className="text-sm text-amber-800 mt-1">{t('dashboard.verifyBanner.body')}</p>
          <div className="mt-3 flex items-center gap-3">
            <button
              onClick={handleResend}
              disabled={resendStatus === 'sending' || resendStatus === 'sent'}
              className="text-sm bg-amber-900 text-white rounded px-3 py-1.5 hover:bg-amber-800 disabled:opacity-50"
            >
              {resendStatus === 'sending'
                ? t('common.working')
                : resendStatus === 'sent'
                  ? t('dashboard.verifyBanner.sent')
                  : t('dashboard.verifyBanner.resend')}
            </button>
            {resendStatus === 'error' && (
              <span className="text-sm text-red-700">{t('error.UNKNOWN')}</span>
            )}
          </div>
        </div>
      )}

      <StorageBar />

      <div className="grid grid-cols-3 gap-4">
        <Link
          to="/items"
          className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow"
        >
          <div className="text-3xl font-bold text-slate-900">{items.length}</div>
          <div className="text-sm text-slate-600 mt-1">{t('dashboard.stats.items')}</div>
        </Link>
        <Link
          to="/categories"
          className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow"
        >
          <div className="text-3xl font-bold text-slate-900">{categories.length}</div>
          <div className="text-sm text-slate-600 mt-1">{t('dashboard.stats.categories')}</div>
        </Link>
        <Link
          to="/locations"
          className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow"
        >
          <div className="text-3xl font-bold text-slate-900">{locations.length}</div>
          <div className="text-sm text-slate-600 mt-1">{t('dashboard.stats.locations')}</div>
        </Link>
      </div>

      {lowStockItems.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-slate-900 mb-3">
            {t('dashboard.lowStock')} ({lowStockItems.length})
          </h2>
          <ul className="space-y-1 text-sm">
            {lowStockItems.map((i) => (
              <li key={i.id} className="flex justify-between">
                <Link to={`/items/${i.id}`} className="text-slate-700 hover:underline">
                  {i.name}
                </Link>
                <span className="text-red-600 font-medium">
                  {Number(i.quantity).toLocaleString('da-DK', { maximumFractionDigits: 3 })}{' '}
                  {i.unit}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {items.length === 0 && (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-slate-600 mb-4">{t('dashboard.empty')}</p>
          {canWrite(user.role) && (
            <Link
              to="/items/new"
              className="inline-block bg-slate-900 text-white rounded px-4 py-2 hover:bg-slate-800 text-sm font-medium"
            >
              {t('dashboard.firstItem')}
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
